import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import useVuelidate from '@vuelidate/core';

import {
  MemberSelector,
  OrderConfig,
  ProgressBar,
} from '@/components/Order';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import {
  DELETE_SCHEDULE,
  GET_DRAFT,
  SET_DEPARTMENT_LIST,
  SET_DRAFT_ORDER,
  UPDATE_DRAFT_ORDER,
  UPDATE_ORDER,
  UPDATE_ORDER_FAIL,
  UPDATE_SCHEDULE,
} from '@/store/orders/constants';
import { toSnakeCase } from '@/utils/caseConvertor';
import {
  ALLOW_EDIT_TYPES,
  DRAFT_GET_TYPES,
  ORDER_STATUS_WITH_TYPE_TEXT,
  ORDER_STEP_MAPPING,
  ORDER_STEP,
  SNACKBAR_TYPES,
} from '@/utils/constants';

export default {
  name: 'EditOrder',
  components: {
    MemberSelector,
    OrderConfig,
    ProgressBar,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      createdOrderId: null,
      currentStepIndex: 0,
      loadingSubmit: false,
      loadingOrder: false,
      stepMapping: ORDER_STEP_MAPPING,
    };
  },
  computed: {
    ...mapGetters('orders', {
      currentOrderId: 'currentDraftId',
      draftBuyingMethod: 'draftBuyingMethod',
      menuId: 'draftMenuId',
      storeId: 'draftStoreId',
    }),
    ...mapState(['isSidebarCollapsed']),
    ...mapState('orders', {
      draftOrder: 'draftOrder',
      isGettingDraft: 'isGettingDraft',
    }),
    allowEditType() {
      if (
        this.draftBuyingMethod === ORDER_STATUS_WITH_TYPE_TEXT.SCHEDULED
        || this.draftBuyingMethod === ORDER_STATUS_WITH_TYPE_TEXT.DRAFT
      ) {
        return ALLOW_EDIT_TYPES.ALL;
      }
      if (this.draftBuyingMethod === ORDER_STATUS_WITH_TYPE_TEXT.ONGOING) {
        return ALLOW_EDIT_TYPES.ONLY_ADD_MEMBERS;
      }
      return ALLOW_EDIT_TYPES.NO_EDIT_MEMBERS;
    },
    canUpdateOrder() {
      return !this.v$.$invalid;
    },
    currentStep: {
      get() {
        return ORDER_STEP[this.currentStepIndex];
      },
      set(step) {
        this.currentStepIndex = this.getStepIndex(step);
      },
    },
    inViewResultMode() {
      const methods = [
        ORDER_STATUS_WITH_TYPE_TEXT.CANCELLED,
        ORDER_STATUS_WITH_TYPE_TEXT.COMPLETED,
      ];
      return methods.includes(this.draftBuyingMethod);
    },
    isLastStep() {
      return this.getStepIndex() >= ORDER_STEP.length - 2;
    },
    isLoading() {
      return this.isGettingDraft || this.loadingOrder;
    },
    isNextStepEnabled() {
      switch (this.currentStep) {
        case ORDER_STEP_MAPPING.MEMBER_SELECTOR:
          return !this.v$.$invalid;
        case ORDER_STEP_MAPPING.ORDER_CONFIG:
          return !this.v$.$invalid;
        default:
          return false;
      }
    },
    isStepMemberSelector() {
      return this.currentStep === ORDER_STEP_MAPPING.MEMBER_SELECTOR;
    },
    isStepOrderConfig() {
      return this.currentStep === ORDER_STEP_MAPPING.ORDER_CONFIG;
    },
    nextStepButtonText() {
      const mapping = {
        [ORDER_STEP_MAPPING.MEMBER_SELECTOR]: '確認並前往下一步',
        [ORDER_STEP_MAPPING.ORDER_CONFIG]: '更新訂購',
      };
      return mapping[this.currentStep] || '';
    },
    orderId: {
      get() {
        return this.currentOrderId;
      },
      set(orderId) {
        this.updateDraftOrder({ draftId: orderId });
      },
    },
    showActionBar() {
      if (this.currentStep === ORDER_STEP_MAPPING.ORDER_CONFIG) {
        return !!Number(this.menuId);
      }
      return this.currentStep === ORDER_STEP_MAPPING.MEMBER_SELECTOR;
    },
    steps() {
      return [
        {
          description: '挑選出想參團一起訂的人員，增加後續訂購的準確性。',
          name: '誰要一起訂',
        },
        {
          description: '選取店家，設好時間一起訂。',
          name: '選擇訂哪家',
        },
        {
          description: '向店家下訂單，分發物品給夥伴。',
          name: '下訂與分發',
        },
      ];
    },
  },
  async created() {
    try {
      this.loadingOrder = true;
      const departments = await this.getDepartmentList();
      const { params: { orderId } = {} } = this.$route || {};
      this.setDepartmentList(departments);
      if (orderId) {
        await this.loadDraft(orderId);
      }
    } catch (error) {
      this.$snackbar({
        type: SNACKBAR_TYPES.ERROR,
        title: '載入資訊失敗，請重新整理網頁。',
        msg: error?.message || '發生未知錯誤，請再試一次',
      });
    } finally {
      this.loadingOrder = false;
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
  beforeDestroy() {
    this.orderId = null;
    this.setDraftOrder({});
  },
  methods: {
    ...mapActions('orders', {
      getDraft: GET_DRAFT,
      deleteSchedule: DELETE_SCHEDULE,
      updateOrder: UPDATE_ORDER,
      updateSchedule: UPDATE_SCHEDULE,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
      setDraftOrder: SET_DRAFT_ORDER,
      updateDraftOrder: UPDATE_DRAFT_ORDER,
      updateOrderFail: UPDATE_ORDER_FAIL,
    }),
    createOrderPayload() {
      const {
        notifyDeadline,
        notifyReserve,
        notifyStart,
        shiftMin,
        ...order
      } = cloneDeep(this.draftOrder);
      const data = {
        ...order,
        notifyDeadline: notifyDeadline || false,
        notifyReserve: notifyReserve || false,
        notifyStart: notifyStart || false,
        shiftMin: shiftMin || 0,
        step: this.currentStep,
      };

      delete data.schedule;

      return { id: this.orderId, data: toSnakeCase(data) };
    },
    getStepIndex(step = this.currentStep) {
      return ORDER_STEP.indexOf(step);
    },
    goBack() {
      const { query: { tab } = {} } = this.$route || {};
      const isValidTab = Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(tab);
      const query = isValidTab ? { tab } : {};
      this.$router.push({
        path: '/order/list',
        query,
      });
    },
    async loadDraft(id) {
      const { menu_id: menuId, store_id: storeId } = await this.getDraft({
        id,
        type: DRAFT_GET_TYPES.EDIT,
      });
      this.updateDraftOrder({
        menuId,
        storeId,
      });
      this.orderId = id;

      this.currentStepIndex = 1;
    },
    nextStep() {
      const currentStepIndex = ORDER_STEP.findIndex((step) => step === this.currentStep);
      this.setStep(ORDER_STEP[currentStepIndex + 1]);
    },
    async onSubmit() {
      if (this.currentStep === ORDER_STEP_MAPPING.ORDER_CONFIG) {
        const success = await this.submitUpdateOrder();
        if (!success) {
          return;
        }
      }

      if (!this.isLastStep) {
        this.nextStep();
        return;
      }

      this.$router.push(`/order/${this.orderId}`);
    },
    setStep(step = ORDER_STEP_MAPPING.STORE_SELECTOR) {
      this.currentStep = step;
    },
    async doScheduleAction() {
      const { draftId: id, enableSchedule, schedule } = this.draftOrder;
      const isUpdateSchedule = enableSchedule && !isEmpty(schedule);
      const isDeleteSchedule = !enableSchedule && !isEmpty(schedule);

      try {
        if (isUpdateSchedule) {
          await this.updateSchedule({ id, data: toSnakeCase(schedule) });
        }
        if (isDeleteSchedule) {
          await this.deleteSchedule(id);
        }
        return true;
      } catch (error) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: `目前無法${isUpdateSchedule ? '更新' : '刪除'}例行性訂購資訊`,
          msg: error?.message || '發生未知錯誤，請再試一次',
        });
        return false;
      }
    },
    async submitUpdateOrder() {
      let success = true;
      try {
        this.loadingSubmit = true;
        this.updateOrderFail(false);
        const order = this.createOrderPayload();

        await this.updateOrder(order);

        this.$snackbar({
          type: SNACKBAR_TYPES.SUCCESS,
          title: '訂購資訊已更新',
        });
      } catch (error) {
        success = false;
        this.updateOrderFail(true);
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '無法更新',
          msg: error?.message || '發生未知錯誤，請再試一次',
        });
      } finally {
        this.loadingSubmit = false;
      }
      return success;
    },
  },
};
